import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigationType } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { EntriesListItem } from './EntriesList';
import CenteredEmptyState from './CenteredEmptyState';
import { buildTimelineListItems } from '../utils/timelineListUtils';
import type { RepositoryEntriesRowDto } from '../services/repositoryEntries';
import type { TimelineSource } from '../hooks/useTimelineSources';

interface Props {
	rows: RepositoryEntriesRowDto[];
	sourceByRowId: Map<string, TimelineSource>;
	originDay: Dayjs;
	selectedRowId: string | null;
	onSelect: (row: RepositoryEntriesRowDto) => void;
	onBackgroundClick: () => void;
	showCollection: boolean;
}

/**
 * Small-screen timeline: entries newest-first as a linear, chat-like list under relative date group
 * labels (see `buildTimelineListItems`). Scrolls with the page, so the browser restores the position
 * on back navigation; any other arrival scrolls to the first group not in the future.
 */
/** One half of the current-moment line, flanking a label on either side. */
const nowLine = <Box aria-hidden sx={{ flex: 1, borderTop: (theme) => `2px dashed ${theme.palette.error.main}` }} />;

function TimelineList({ rows, sourceByRowId, originDay, selectedRowId, onSelect, onBackgroundClick, showCollection }: Props) {
	const anchorRef = useRef<HTMLDivElement | null>(null);
	const navigationType = useNavigationType();

	const items = useMemo(
		() => buildTimelineListItems(
			rows.map((row) => ({
				entry: row,
				key: row.id,
				date: dayjs(row.data[sourceByRowId.get(row.id)?.dateProperty?.slug ?? ''] as string),
			})),
			originDay,
			dayjs(),
		),
		[rows, sourceByRowId, originDay],
	);

	useLayoutEffect(() => {
		if (navigationType === 'POP') return;
		anchorRef.current?.scrollIntoView({ block: 'start' });
	}, [navigationType]);

	return (
		<Box
			onClick={onBackgroundClick}
			sx={{ minHeight: 120, position: 'relative', display: 'flex', flexDirection: 'column', gap: 1, p: 1, pb: 9 }}
		>
			{!rows.length && <CenteredEmptyState />}
			{items.map((item) => {
				if (item.kind === 'gap') {
					return (
						<Box key={item.key} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, mt: 1 }}>
							<Box aria-hidden sx={{ height: 16, borderLeft: (theme) => `2px dotted ${theme.palette.text.disabled}` }} />
							<Typography variant="caption" color="text.disabled" sx={{ py: 0.25 }}>
								{item.label}
							</Typography>
							<Box aria-hidden sx={{ height: 16, borderLeft: (theme) => `2px dotted ${theme.palette.text.disabled}` }} />
						</Box>
					);
				}
				if (item.kind === 'now') {
					return (
						<Box key={item.key} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, py: 0.5 }}>
							{nowLine}
							<Typography variant="caption" color="error" sx={{ px: 1.5 }}>Now</Typography>
							{nowLine}
						</Box>
					);
				}
				if (item.kind === 'marker') {
					return (
						<Box
							key={item.key}
							ref={item.isAnchor ? anchorRef : undefined}
							sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 0.5, scrollMarginTop: 8 }}
						>
							{item.isNow && nowLine}
							<Typography
								variant="caption"
								sx={{
									// bgcolor: 'background.paper',
									color: 'primary.main',
									borderRadius: 999,
									px: 1.5,
									py: 0.25,
									textAlign: 'center',
									// boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
								}}
							>
								{item.secondary}
								<Box component="span" sx={{ display: 'block', opacity: 0.7 }}>{item.primary}</Box>
							</Typography>
							{item.isNow && nowLine}
						</Box>
					);
				}
				const row = item.entry;
				const source = sourceByRowId.get(row.id);
				return (
					<EntriesListItem
						key={item.key}
						id={row.id}
						label={row.label}
						entry={row}
						propertyDefinitions={source?.propertyDefinitions ?? []}
						repositorySingular={showCollection ? (source?.repository?.singular ?? undefined) : undefined}
						collectionColor={source?.repository?.color ?? undefined}
						disabled={Boolean(row.isArchived)}
						isSelected={selectedRowId === row.id}
						onClick={(e) => {
							e.stopPropagation();
							onSelect(row);
						}}
					/>
				);
			})}
		</Box>
	);
}

export default TimelineList;
